import { floorWith, isDITModelType, isDistillModelType } from "./util.mjs";
import { BasePricing, GenerationModelTypeSubSet } from "./base.mjs";
//#region src/imageGen.ts
const Scenario = {
	Enlarge: "enlarge",
	ControlNetTile: "control-net-tile",
	Hires: "hires",
	NormalT2i: "normal-t2i",
	EnableAdetailer: "face-fix",
	IpAdapter: "ip-adapter"
};
var BaseDiffusionModelPricing = class BaseDiffusionModelPricing extends BasePricing {
	static getGenerationScenarios(parameters) {
		const { enlarge, upscale, enableTile, enableADetailer, ipAdapter } = parameters;
		const scenarios = [];
		if (enlarge > 1) scenarios.push(Scenario.Enlarge);
		else if (enableTile && upscale > 1) scenarios.push(Scenario.ControlNetTile);
		else if (upscale > 1) scenarios.push(Scenario.Hires);
		else scenarios.push(Scenario.NormalT2i);
		if (enableADetailer) scenarios.push(Scenario.EnableAdetailer);
		if (ipAdapter) scenarios.push(Scenario.IpAdapter);
		return scenarios;
	}
	static calcModelDiscountPrice(parameters, config) {
		const { modelId: modelVersionId } = parameters;
		const { modelVersionDiscount } = config;
		if (!modelVersionId || !modelVersionDiscount) return;
		if (!modelVersionDiscount[modelVersionId]) return;
		const discountPercent = modelVersionDiscount[modelVersionId] / 100;
		return {
			cumulative: (i) => i * discountPercent,
			item: {
				key: "model-discount",
				name: "model discount"
			}
		};
	}
	calcDiffusionPipelinePrice(parameters, config, meter = this.meter) {
		const mergedConfig = {
			...this.config,
			...config
		};
		meter.addResult(this.calcModelMultiplierPrice());
		meter.addResult(BaseDiffusionModelPricing.calcImageSizePrice(parameters, mergedConfig));
		meter.addResult(this.calcStepPowerPrice(parameters, mergedConfig));
		meter.addResult(BaseDiffusionModelPricing.calcSamplerPowerPrice(parameters, mergedConfig));
		meter.addResult(BaseDiffusionModelPricing.calcControlNetPrice(parameters));
		meter.addResult(BaseDiffusionModelPricing.calcFloorBeforeTimesBatchSize(mergedConfig));
		meter.addResult(BaseDiffusionModelPricing.calcBatchSizePrice(parameters));
	}
	static calcFloorBeforeTimesBatchSize(config) {
		const { floorBeforeTimesBatchSize } = config;
		if (floorBeforeTimesBatchSize) return {
			cumulative: (i) => floorWith(i, 100),
			item: {
				key: "floor",
				name: "floor",
				hidden: true
			}
		};
	}
	static calcControlNetPrice(parameters) {
		const { controlNets } = parameters;
		if (Array.isArray(controlNets) && controlNets.length) return {
			cumulative: (i) => i * 1.4 ** (controlNets.length - 1),
			item: {
				key: "control-nets",
				name: "control nets"
			}
		};
	}
	static calcSamplerPowerPrice(parameters, config) {
		const { samplingMethod } = parameters;
		const { samplerMultiple } = config;
		const samplerPower = samplerMultiple[samplingMethod] ?? 1;
		if (samplerPower !== 1) return {
			cumulative: (i) => i * samplerPower,
			item: {
				key: "sampling-method",
				name: "sampling method"
			}
		};
	}
	static calcBatchSizePrice(parameters) {
		const { batchSize } = parameters;
		if (batchSize !== 1) return {
			cumulative: (i) => i * batchSize,
			item: {
				key: "batch-size",
				name: "batch size"
			}
		};
	}
	static calcImageSizePrice(parameters, config) {
		const { width, height, upscale: _upscale, enableTile } = parameters;
		const { baseWidth, baseHeight } = config;
		let upscale = _upscale;
		if (!Number.isFinite(upscale) || upscale < 1) upscale = 1;
		const sizePower = enableTile ? 1 : width * height * upscale * upscale / baseWidth / baseHeight;
		if (sizePower !== 1) return {
			cumulative: (i) => i * sizePower,
			item: {
				key: "size",
				name: "size"
			}
		};
	}
	static calcPriorityChanelPrice(parameters, config) {
		const { priority } = parameters;
		const { highPriority, xHighPriority } = config;
		if (priority >= highPriority || priority >= xHighPriority) {
			let priorityFee = highPriority;
			if (priority > highPriority) priorityFee = xHighPriority;
			if (priority > xHighPriority) priorityFee = priority;
			return {
				cumulative: (i) => i + priorityFee,
				item: {
					key: "task-priority",
					name: "priority channel"
				}
			};
		}
	}
};
var SDBasedPricing = class extends BaseDiffusionModelPricing {
	getFinalPrice() {
		this.calcBasePrice();
		const scenarios = BaseDiffusionModelPricing.getGenerationScenarios(this.parameters);
		if (scenarios.includes(Scenario.Enlarge)) {} else if (scenarios.includes(Scenario.ControlNetTile)) this.calcTilePrice();
		else if (scenarios.includes(Scenario.Hires)) this.calcHiresPrice();
		else this.calcDiffusionPipelinePrice(this.parameters, {
			...this.config,
			floorBeforeTimesBatchSize: true,
			baseStep: 28
		});
		if (scenarios.includes(Scenario.EnableAdetailer)) this.meter.addResult(this.calcADetailerPrice());
		this.meter.addResult(this.calcMinusBasePrice());
		this.meter.addResult(this.calcBatchDiscountForVerifiedUsersPrice());
		this.meter.addResult(this.calcIpAdapterPrice());
		this.meter.addResult(BaseDiffusionModelPricing.calcModelDiscountPrice(this.parameters, this.config));
		this.meter.addResult(this.calcMaxDiscountPrice());
		this.meter.addResult(this.calcMinusPrice());
		this.meter.addResult(BaseDiffusionModelPricing.calcPriorityChanelPrice(this.parameters, this.config));
		this.meter.addResult(this.calcBatchSizeGreaterThan4Price());
		return this.meter;
	}
	calcBatchSizeGreaterThan4Price() {
		const { batchSize } = this.parameters;
		const { minPricePerImageForUnknownModel } = this.config;
		if (batchSize > 4 && this.meter.curPrice < batchSize * minPricePerImageForUnknownModel) return {
			cumulative: () => batchSize * minPricePerImageForUnknownModel,
			item: {
				key: "unknown-model-min-price-large-batch",
				name: "unknown model min price large batch"
			}
		};
	}
	calcMinusPrice() {
		const { minPrice } = this.config;
		if (minPrice > 0 && this.meter.curPrice < minPrice) return {
			cumulative: () => minPrice,
			item: {
				key: "min-price",
				name: "min price"
			}
		};
	}
	calcMaxDiscountPrice() {
		const { maxDiscount } = this.config;
		if (this.meter.curPrice < -maxDiscount) return {
			cumulative: () => -maxDiscount,
			item: {
				key: "max-discount",
				name: "max discount"
			}
		};
	}
	calcIpAdapterPrice() {
		const { ipAdapter } = this.parameters;
		if (!ipAdapter || !ipAdapter.enabled) return void 0;
		return {
			cumulative: (i) => i + 500,
			item: {
				key: "wdtagger",
				name: "wdtagger",
				unitPrice: 500
			}
		};
	}
	calcBatchDiscountForVerifiedUsersPrice() {
		const { batchSize } = this.parameters;
		const { shouldDiscount } = this.config;
		if (batchSize > 1 && this.meter.curPrice > 0 && shouldDiscount) return {
			cumulative: (i) => i * .5,
			item: {
				key: "email-verification-discount",
				name: "email verification discount"
			}
		};
	}
	calcMinusBasePrice() {
		const { batchSize } = this.parameters;
		const actualBasePrice = this.getBasePrice();
		return {
			cumulative: (i) => floorWith(i - actualBasePrice * batchSize, 100),
			item: {
				key: "minus-base-price",
				name: "minus base price",
				hidden: true
			}
		};
	}
	calcADetailerPrice() {
		const adMeter = this.getNewPriceMeter();
		const actualBasePrice = this.getBasePrice();
		adMeter.addItem(() => actualBasePrice, {
			key: "generate-image",
			name: "generate image"
		});
		this.calcDiffusionPipelinePrice({
			...this.parameters,
			height: 512,
			width: 512,
			upscale: 1,
			enlarge: 1,
			strength: .8,
			mediaId: "1",
			controlNets: []
		}, this.config, adMeter);
		const adPrice = adMeter.toPriceInfo();
		return {
			cumulative: (i) => i + adPrice.actualPrice,
			item: {
				key: "adetailer",
				name: "adetailer",
				unitPrice: adPrice.actualPrice
			}
		};
	}
	calcHiresUpscalePrice() {
		this.calcDiffusionPipelinePrice({
			...this.parameters,
			upscale: 1
		}, this.config);
		const hiresMeter = this.getNewMeterForHires();
		this.calcDiffusionPipelinePrice(this.parameters, this.config, hiresMeter);
		const hiresPrice = hiresMeter.toPriceInfo();
		return {
			cumulative: (i) => i + hiresPrice.actualPrice,
			item: {
				key: "upscale",
				name: "upscale",
				items: hiresPrice.items
			}
		};
	}
	calcHiresDiscountPrice(hiresActualPrice) {
		const discount = .5;
		return {
			cumulative: (i) => i - hiresActualPrice * discount,
			item: {
				key: "upscale-discount",
				name: "upscale discount"
			}
		};
	}
	calcHiresPrice() {
		const upscaleResult = this.calcHiresUpscalePrice();
		this.meter.addResult(upscaleResult);
		const hiresMeter = this.getNewMeterForHires();
		this.calcDiffusionPipelinePrice(this.parameters, this.config, hiresMeter);
		const hiresActualPrice = hiresMeter.toPriceInfo().actualPrice;
		this.meter.addResult(this.calcHiresDiscountPrice(hiresActualPrice));
	}
	calcTilePriceItem() {
		const { width, height, upscale } = this.parameters;
		const { baseHeight, baseWidth } = this.config;
		const tileMeter = this.getNewMeterForHires();
		const tileTimes = Math.ceil(height * upscale * (width * upscale) / (baseHeight * baseWidth));
		this.calcDiffusionPipelinePrice(this.parameters, this.config, tileMeter);
		const tilePrice = tileMeter.toPriceInfo();
		return {
			cumulative: (i) => i + tilePrice.actualPrice * tileTimes,
			item: {
				key: "tile",
				name: "tile",
				unitPrice: tilePrice.actualPrice,
				items: tilePrice.items
			}
		};
	}
	calcTilePrice() {
		this.meter.addResult(this.calcTilePriceItem());
	}
	calcModelMultiplierPrice() {}
	calcStepPowerPrice(parameters, config) {
		const { samplingSteps, strength, mediaId, upscaleDenoisingSteps = samplingSteps, upscale } = parameters;
		const { baseStep } = config;
		let equivalentStep = samplingSteps;
		if (!!mediaId) equivalentStep = 1 + strength * samplingSteps;
		else if (upscale > 1) equivalentStep = upscaleDenoisingSteps;
		else equivalentStep = samplingSteps;
		const stepPower = equivalentStep / baseStep;
		if (stepPower !== 1) return {
			cumulative: (i) => i * stepPower,
			item: {
				key: "sampling-steps",
				name: "sampling steps"
			}
		};
	}
	getBasePrice() {
		const { basePrice } = this.config;
		return basePrice;
	}
	calcBasePriceItem() {
		const actualBasePrice = this.getBasePrice();
		return {
			cumulative: () => actualBasePrice,
			item: {
				key: "generate-image",
				name: "generate image"
			}
		};
	}
	calcBasePrice() {
		this.meter.addResult(this.calcBasePriceItem());
		return this.meter;
	}
	getNewMeterForHires() {
		const actualBasePrice = this.getBasePrice();
		const meter = this.getNewPriceMeter();
		meter.addItem(() => actualBasePrice, {
			key: "generate-image",
			name: "generate image"
		});
		return meter;
	}
};
var SDXLImageGenPricing = class extends SDBasedPricing {
	calcModelMultiplierPrice() {
		const { isSDXL, modelType, sdxlMultiple } = this.config;
		if (isSDXL || modelType === GenerationModelTypeSubSet.SdxlModel) return {
			cumulative: (i) => i * sdxlMultiple,
			item: {
				key: "sdxl",
				name: "sdxl"
			}
		};
	}
	calcBatchSizeGreaterThan4Price() {
		const { batchSize } = this.parameters;
		const { minPricePerSDImage } = this.config;
		if (batchSize > 4 && this.meter.curPrice < batchSize * minPricePerSDImage) return {
			cumulative: () => batchSize * minPricePerSDImage,
			item: {
				key: "sd-model-min-price-large-batch",
				name: "sd model min price large batch"
			}
		};
	}
};
var DITImageGenPricing = class extends SDBasedPricing {
	getBasePrice() {
		const { basePrice } = this.config;
		let actualBasePrice;
		if (!!this.parameters.mediaId) actualBasePrice = basePrice * 2;
		else {
			const { width, height, samplingSteps } = this.parameters;
			actualBasePrice = basePrice * ([
				[768, 1280],
				[1280, 768],
				[1024, 1024]
			].some(([w, h]) => w === width && h === height) && samplingSteps >= 20 && samplingSteps <= 25 ? 1.5 : 1.6);
		}
		return actualBasePrice;
	}
	calcMinusPrice() {
		const { batchSize } = this.parameters;
		const { ditMinPrice } = this.config;
		if (batchSize <= 4 && this.meter.curPrice < ditMinPrice) return {
			cumulative: () => ditMinPrice,
			item: {
				key: "dit-min-price",
				name: "dit min price"
			}
		};
	}
	calcBatchSizeGreaterThan4Price() {
		const { batchSize } = this.parameters;
		const { minPricePerDitImage } = this.config;
		if (batchSize > 4 && this.meter.curPrice < batchSize * minPricePerDitImage) return {
			cumulative: () => batchSize * minPricePerDitImage,
			item: {
				key: "dit-model-min-price-large-batch",
				name: "dit model min price large batch"
			}
		};
	}
	calcModelMultiplierPrice() {
		const { modelType, sdxlMultiple } = this.config;
		const ditMultiple = sdxlMultiple;
		if (isDITModelType(modelType)) return {
			cumulative: (i) => i * ditMultiple,
			item: {
				key: "dit",
				name: "dit"
			}
		};
	}
	calcStepPowerPrice(parameters, config) {
		let { samplingSteps, upscaleDenoisingSteps = samplingSteps, mediaId, strength, upscale, modelId } = parameters;
		const { pricingConfig, baseStep } = config;
		if (isDistillModelType(pricingConfig?.variant, modelId)) {
			samplingSteps = samplingSteps + 5;
			upscaleDenoisingSteps = upscaleDenoisingSteps + 5;
		}
		let equivalentStep = samplingSteps;
		if (!!mediaId) equivalentStep = 1 + strength * samplingSteps;
		else if (upscale > 1) equivalentStep = upscaleDenoisingSteps;
		else equivalentStep = samplingSteps;
		const stepPower = equivalentStep / baseStep;
		if (stepPower !== 1) return {
			cumulative: (i) => i * stepPower,
			item: {
				key: "sampling-steps",
				name: "sampling steps"
			}
		};
	}
	calcHiresUpscalePrice() {
		this.calcDiffusionPipelinePrice({
			...this.parameters,
			upscale: 1
		}, this.config);
		const hiresMeter = this.getNewMeterForHires();
		this.calcDiffusionPipelinePrice(this.parameters, {
			...this.config,
			modelType: GenerationModelTypeSubSet.SdxlModel
		}, hiresMeter);
		const hiresPrice = hiresMeter.toPriceInfo();
		return {
			cumulative: (i) => i + hiresPrice.actualPrice,
			item: {
				key: "upscale",
				name: "upscale",
				items: hiresPrice.items
			}
		};
	}
	calcHiresPrice() {
		this.meter.addResult(this.calcHiresUpscalePrice());
		const hiresMeter = this.getNewMeterForHires();
		this.calcDiffusionPipelinePrice(this.parameters, {
			...this.config,
			modelType: GenerationModelTypeSubSet.SdxlModel
		}, hiresMeter);
		const hiresActualPrice = hiresMeter.toPriceInfo().actualPrice;
		this.meter.addResult(this.calcHiresDiscountPrice(hiresActualPrice));
	}
	getNewMeterForHires() {
		const { basePrice } = this.config;
		const meter = this.getNewPriceMeter();
		meter.addItem(() => basePrice, {
			key: "generate-image",
			name: "generate image"
		});
		return meter;
	}
};
var MMDIT26AImageGenPricing = class extends BasePricing {
	getFinalPrice() {
		this.meter.addResult(this.calcBasePriceItem());
		this.meter.addResult(this.calcImageSizePrice());
		this.meter.addResult(BaseDiffusionModelPricing.calcFloorBeforeTimesBatchSize({
			...this.config,
			floorBeforeTimesBatchSize: true
		}));
		this.meter.addResult(BaseDiffusionModelPricing.calcBatchSizePrice(this.parameters));
		this.meter.addResult(BaseDiffusionModelPricing.calcModelDiscountPrice(this.parameters, this.config));
		this.meter.addResult(this.calcLoraCampaignDiscount());
		this.meter.addResult(this.calcBatchSizeDiscountForVerifiedUsersPrice());
		this.meter.addResult(this.calcWatermarkDiscountPrice());
		this.meter.addResult(BaseDiffusionModelPricing.calcPriorityChanelPrice(this.parameters, this.config));
		this.meter.addResult(this.calcMinusPrice());
		return this.meter;
	}
	getBasePrice() {
		const { inferenceProfile } = this.parameters;
		const { mmdit26aLiteBasePrice = 1100, mmdit26aStandardBasePrice = 1600, mmdit26aProBasePrice = 3400, mmdit26aUltraBasePrice = 3500 } = this.config;
		if (inferenceProfile === "lite") return mmdit26aLiteBasePrice;
		if (inferenceProfile === "standard") return mmdit26aStandardBasePrice;
		if (inferenceProfile === "pro") return mmdit26aProBasePrice;
		if (inferenceProfile === "ultra") return mmdit26aUltraBasePrice;
		return mmdit26aStandardBasePrice;
	}
	calcBasePriceItem() {
		const actualBasePrice = this.getBasePrice();
		return {
			cumulative: () => actualBasePrice,
			item: {
				key: "generate-image",
				name: "generate image"
			}
		};
	}
	calcImageSizePrice() {
		const { width, height } = this.parameters;
		const ratio = width * height / (768 * 1280);
		const sizePower = ratio > 1 ? ratio : 1;
		return {
			cumulative: (i) => i * sizePower,
			item: {
				key: "size",
				name: "size"
			}
		};
	}
	calcBatchSizeDiscountForVerifiedUsersPrice() {
		const { batchSize } = this.parameters;
		const { shouldDiscount } = this.config;
		if (batchSize > 1 && shouldDiscount) return {
			cumulative: (i) => i * .5,
			item: {
				key: "email-verification-discount",
				name: "email verification discount"
			}
		};
	}
	calcWatermarkDiscountPrice() {
		const { watermark } = this.parameters;
		const { mmdit26aWatermarkDiscount = .9 } = this.config;
		if (watermark && ["pixai"].includes(watermark)) return {
			cumulative: (i) => i * mmdit26aWatermarkDiscount,
			item: {
				key: "watermark-discount",
				name: "watermark discount"
			}
		};
	}
	calcLoraCampaignDiscount() {
		const { loraCampaign } = this.config;
		if (!loraCampaign) return;
		const { name, loraVersionIds: campaignLoraIds, discount = .9, startTime, endTime } = loraCampaign;
		const now = Date.now();
		if (now < new Date(startTime).getTime() || now > new Date(endTime).getTime()) return;
		const { loraParameters = [] } = this.parameters;
		if (!loraParameters.some((l) => campaignLoraIds.includes(l.versionId))) return;
		return {
			cumulative: (i) => i * discount,
			item: {
				key: name,
				name
			}
		};
	}
	calcMinusPrice() {
		const { mmdit26aMinPrice = 1e3 } = this.config;
		if (mmdit26aMinPrice > 0 && this.meter.curPrice < mmdit26aMinPrice) return {
			cumulative: () => mmdit26aMinPrice,
			item: {
				key: "min-price",
				name: "min price"
			}
		};
	}
};
//#endregion
export { DITImageGenPricing, MMDIT26AImageGenPricing, SDBasedPricing, SDXLImageGenPricing };
